import { useState, useCallback, useEffect, useMemo } from 'react';
import { 
    PendingThresholdChange,
    FilterOptions,
    SortOption,
    ValidationErrors,
    R2FProductionCapabilityData,
} from 'src/features/types/r2fTypes';

export interface UseR2FThresholdTableStateProps {
    data: {
        productionCapabilities: R2FProductionCapabilityData[];
    };
    externalPendingChanges?: PendingThresholdChange[];
    userCanEdit: boolean;
    onSave: (changes: PendingThresholdChange[]) => Promise<void>;
    isPSSD?: boolean;
}

/**
 * Custom hook for R2F threshold table state management
 */
export const useR2FThresholdTableState = ({
    data,
    externalPendingChanges,
    userCanEdit,
    onSave,
    isPSSD
}: UseR2FThresholdTableStateProps) => {
    // State for tracking which production capabilities are expanded - default to all expanded
    const [expandedProductionCapabilities, setExpandedProductionCapabilities] = useState<Record<string, boolean>>({});
    
    // Set all production capabilities to expanded by default when data changes
    useEffect(() => {
        const initialExpandState: Record<string, boolean> = {};
        data.productionCapabilities.forEach(pc => {
            initialExpandState[pc.id] = true;
        });
        setExpandedProductionCapabilities(initialExpandState);
    }, [data.productionCapabilities]);
    
    // State for pending changes to threshold values
    const [pendingChanges, setPendingChanges] = useState<Record<string, Record<string, PendingThresholdChange>>>({});
    
    // State for validation tracking
    const [invalidRows, setInvalidRows] = useState<Set<string>>(new Set());
    
    // State for filters and sorting
    const [filters, setFilters] = useState<FilterOptions>({
        productionCapability: '',
        handlingTier: '',
        status: ''
    });
    
    const [sorting, setSorting] = useState<SortOption>({
        field: 'productionCapability',
        direction: 'asc'
    });
    
    // State to track which production capabilities should automatically show the add node UI
    const [autoShowAddingFor, setAutoShowAddingFor] = useState<Record<string, boolean>>({});

    // Process external pendingChanges if provided
    useEffect(() => {
        if (externalPendingChanges && externalPendingChanges.length > 0) {
            // Process these changes and integrate them into the internal pendingChanges state
            const processedChanges: Record<string, Record<string, PendingThresholdChange>> = {};
            
            externalPendingChanges.forEach(change => {
                // Create a synthetic ID for each pending change based on productionCapability
                const syntheticId = `pending_${change.productionCapability.toLowerCase().replace(/\s+/g, '_')}`;
                const changeKey = `${syntheticId}_${change.handlingTier.toLowerCase().replace(/\s+/g, '_')}`;
                
                // Add to the structure
                if (!processedChanges[syntheticId]) {
                    processedChanges[syntheticId] = {};
                }
                
                processedChanges[syntheticId][changeKey] = change;
            });
            
            // Merge with existing pending changes
            setPendingChanges(prev => {
                const merged = { ...prev };
                
                // Add all processed changes
                Object.entries(processedChanges).forEach(([pcId, changes]) => {
                    if (!merged[pcId]) {
                        merged[pcId] = {};
                    }
                    
                    Object.entries(changes).forEach(([handlingTierId, change]) => {
                        merged[pcId][handlingTierId] = change;
                    });
                });
                
                return merged;
            });
        }
    }, [externalPendingChanges]);
    
    // Toggle expansion state for a production capability
    const handleToggleExpand = useCallback((productionCapabilityId: string) => {
        setExpandedProductionCapabilities(prev => ({
            ...prev,
            [productionCapabilityId]: !prev[productionCapabilityId]
        }));
    }, []);

    // Validate a threshold change
    const validateThresholdChange = useCallback((low: number, high: number): ValidationErrors => {
        return {
            lowHighInvalid: low >= high,
            negativeLowThreshold: low < 0,
            negativeHighThreshold: high < 0,
            duplicateHandlingTier: false // This would be checked when adding a new node
        };
    }, []);

    // Check if a change is valid
    const isChangeValid = useCallback((low: number, high: number): boolean => {
        const validationResult = validateThresholdChange(low, high);
        return !(validationResult.lowHighInvalid || 
                validationResult.negativeLowThreshold || 
                validationResult.negativeHighThreshold);
    }, [validateThresholdChange]);

    // Update invalid rows whenever pendingChanges changes
    useEffect(() => {
        const newInvalidRows = new Set<string>();
        
        Object.entries(pendingChanges).forEach(([pcId, nodeChanges]) => {
            Object.entries(nodeChanges).forEach(([handlingTierId, change]) => {
                // Skip validation for delete operations
                if (change.action !== 'Delete' && !isChangeValid(change.lowThreshold, change.highThreshold)) {
                    newInvalidRows.add(`${pcId}_${handlingTierId}`);
                }
            });
        });
        
        setInvalidRows(newInvalidRows);
    }, [pendingChanges, isChangeValid]);

    // Handle threshold value changes
    const handleValueChange = useCallback((
        productionCapabilityId: string,
        handlingTier: string,
        low: number,
        high: number, 
        action: string,
        reason?: string,
        actionSource?: string
    ) => {
        // Process value changes for thresholds

        setPendingChanges((prev: Record<string, Record<string, PendingThresholdChange>>): Record<string, Record<string, PendingThresholdChange>> => {
            // Find the production capability this belongs to
            const pc = data.productionCapabilities.find(pc => pc.id === productionCapabilityId);
            // Check if production capability exists
            if (!pc) {
                // Production capability not found, return unchanged state
                return prev;
            }
            
            // Get current changes for this production capability
            const pcChanges = prev[productionCapabilityId] || {};
            
            // Handle special cases based on action type
            switch(action) {
                case 'NoChange': {
                    // Remove the pending change
                    const newPCChanges = { ...pcChanges };
                    delete newPCChanges[handlingTier];
                    
                    // If no more changes for this production capability, remove the PC entry
                    if (Object.keys(newPCChanges).length === 0) {
                        const newChanges = { ...prev };
                        delete newChanges[productionCapabilityId];
                        return newChanges;
                    }
                    
                    return {
                        ...prev,
                        [productionCapabilityId]: newPCChanges
                    };
                }
                
                case 'Add': {
                    // For newly added nodes (may not exist in the data yet)
                    const existingChange = pcChanges[handlingTier];
                    
                    if (existingChange) {
                        // Update an existing Add change
                        // Update existing 'Add' change
                        return {
                            ...prev,
                            [productionCapabilityId]: {
                                ...pcChanges,
                                [handlingTier]: {
                                    ...existingChange,
                                    lowThreshold: low,
                                    highThreshold: high
                                }
                            }
                        };
                    }
                    
                    // Create a new Add change for this node
                    
                    // Check if this is a case where we're adding a brand new tier to an existing PC
                    // If handlingTier doesn't match any existing node ID, it's a raw tier name
                    const existingNode = pc.defaultNode?.id === handlingTier 
                        ? pc.defaultNode 
                        : pc.additionalNodes.find(n => n.id === handlingTier);
                    const isRawHandlingTier = !existingNode;
                    
                    if (isRawHandlingTier) {
                        // Handle raw handling tier addition
                        
                        // This is a raw handling tier addition (e.g., "rush")
                        const newChange = {
                            productionCapability: pc.name,
                            handlingTier: handlingTier, // Use the exact handlingTier
                            lowThreshold: low,
                            highThreshold: high,
                            action: 'Add' as const,
                            isDefault: false,
                            reason: reason || `Adding ${handlingTier} handling tier`,
                            actionSource: actionSource || 'Newton Dashboard'
                        };
                        
                        // Created new change entry
                        
                        return {
                            ...prev,
                            [productionCapabilityId]: {
                                ...pcChanges,
                                [handlingTier]: newChange
                            }
                        };
                    }
                    
                    // This should rarely happen - Adding a node that's not in pendingChanges and is not a raw tier ID
                    // Find node info for the change
                    const isDefault = pc.defaultNode?.id === handlingTier;
                    const node = isDefault && pc.defaultNode
                        ? pc.defaultNode
                        : pc.additionalNodes.find(n => n.id === handlingTier);
                    
                    // If node not found, return original state unchanged
                    if (!node) {
                        console.error(`📦 [R2F-TABLE] ERROR: Node ${handlingTier} not found in production capability!`);
                        return {...prev};
                    }
                    
                    // Using node data for Add change
                    
                    return {
                        ...prev,
                        [productionCapabilityId]: {
                            ...pcChanges,
                            [handlingTier]: {
                                productionCapability: pc.name,
                                handlingTier: node.handlingTier,
                                lowThreshold: low,
                                highThreshold: high,
                                currentLowThreshold: node.lowThreshold,
                                currentHighThreshold: node.highThreshold,
                                action: 'Add',
                                isDefault
                            }
                        }
                    };
                }
                
                case 'Delete': {
                    // Find the node being deleted
                    const isDefault = pc.defaultNode?.id === handlingTier;
                    const node = isDefault && pc.defaultNode
                        ? pc.defaultNode
                        : pc.additionalNodes.find(n => n.id === handlingTier);
                    
                    if (!node) return {...prev};
                    
                    // Add a delete pending change
                    return {
                        ...prev,
                        [productionCapabilityId]: {
                            ...pcChanges,
                            [handlingTier]: {
                                productionCapability: pc.name,
                                handlingTier: node.handlingTier,
                                lowThreshold: low,
                                highThreshold: high,
                                currentLowThreshold: node.lowThreshold,
                                currentHighThreshold: node.highThreshold,
                                action: 'Delete',
                                isDefault,
                                reason,
                                actionSource
                            }
                        }
                    };
                }
                
                case 'Modify': {
                    // Find the node being modified
                    const isDefault = pc.defaultNode?.id === handlingTier;
                    const node = isDefault && pc.defaultNode
                        ? pc.defaultNode
                        : pc.additionalNodes.find(n => n.id === handlingTier);
                    
                    if (!node) return {...prev};

                    // Check if values have actually changed
                    const currentLow = node.lowThreshold ?? 0;
                    const currentHigh = node.highThreshold ?? 100;
                    const valuesChanged = low !== currentLow || high !== currentHigh;
                    
                    // If values haven't changed, treat it as "NoChange" to remove any existing pending change
                    if (!valuesChanged) {
                        // No change detected, values are unchanged
                        
                        // Remove the pending change
                        const newPCChanges = { ...pcChanges };
                        delete newPCChanges[handlingTier];
                        
                        // If no more changes for this production capability, remove the PC entry
                        if (Object.keys(newPCChanges).length === 0) {
                            const newChanges = { ...prev };
                            delete newChanges[productionCapabilityId];
                            return newChanges;
                        }
                        
                        return {
                            ...prev,
                            [productionCapabilityId]: newPCChanges
                        };
                    }
                    
                    // Values have changed, so add/update the pending change
                    
                    return {
                        ...prev,
                        [productionCapabilityId]: {
                            ...pcChanges,
                            [handlingTier]: {
                                productionCapability: pc.name,
                                handlingTier: node.handlingTier,
                                lowThreshold: low,
                                highThreshold: high,
                                currentLowThreshold: node.lowThreshold,
                                currentHighThreshold: node.highThreshold,
                                action: 'Modify',
                                isDefault
                            }
                        }
                    };
                }
                
                default:
                    // Any unhandled action type just returns the previous state
                    return prev;
            }
        });
    }, [data.productionCapabilities]);

    // Handle adding a new node to a production capability
    const handleAddNode = useCallback((productionCapabilityId: string, handlingTier: string) => {
        // Clear the autoShowAddingFor flag for this production capability
        setAutoShowAddingFor(prev => {
            const newState = { ...prev };
            delete newState[productionCapabilityId];
            return newState;
        });
        
        // Find the production capability
        const pc = data.productionCapabilities.find(pc => pc.id === productionCapabilityId);
        if (!pc) return;
        
        // Check if this handlingTier already exists
        const existingHandlingTiers = [
            ...pc.defaultNode ? [pc.defaultNode.handlingTier] : [],
            ...pc.additionalNodes.map(n => n.handlingTier)
        ];

        if (existingHandlingTiers.includes(handlingTier)) return;

        // Important: Use the exact handlingTier - don't transform it
        // This is critical to ensure we can match it in the backend
        const newHandlingTier = handlingTier;
        
        // Ensure the production capability is expanded to see the new node
        setExpandedProductionCapabilities(prev => ({
            ...prev,
            [productionCapabilityId]: true
        }));
        
        // Create default values that will be used - these need to be tracked consistently
        const defaultLowThreshold = 0;
        const defaultHighThreshold = 100;
        
        // Add the node as a pending change directly in the state
        setPendingChanges(prev => {
            // Get current changes for this production capability
            const pcChanges = prev[productionCapabilityId] || {};
            
            // Create the change entry with default values
            const change = {
                productionCapability: pc.name,
                handlingTier: handlingTier,
                lowThreshold: defaultLowThreshold,
                highThreshold: defaultHighThreshold,
                action: 'Add' as const,
                isDefault: false,
                reason: 'Add new handling tier'  // Add reason for audit logs
            };
            
            // Return the updated changes
            return {
                ...prev,
                [productionCapabilityId]: {
                    ...pcChanges,
                    [newHandlingTier]: change
                }
            };
        });
    }, [data, setExpandedProductionCapabilities]);

    // Handle deleting a node from a production capability
    const handleDeleteNode = useCallback((productionCapabilityId: string, handlingTier: string) => {
        setPendingChanges(prev => {
            // Get existing changes for this production capability
            const pcChanges = prev[productionCapabilityId] || {};
            
            // Check if this is a newly added node (only exists in pendingChanges)
            const existingChange = pcChanges[handlingTier];
            
            // If this is a pending Add node, just remove it from the pending changes
            if (existingChange && existingChange.action === 'Add') {
                const newPCChanges = { ...pcChanges };
                delete newPCChanges[handlingTier];
                
                // If no more changes for this production capability, remove the PC entry
                if (Object.keys(newPCChanges).length === 0) {
                    const newChanges = { ...prev };
                    delete newChanges[productionCapabilityId];
                    return newChanges;
                }
                
                // Otherwise just update the specific PC's changes
                return {
                    ...prev,
                    [productionCapabilityId]: newPCChanges
                };
            }
            
            // Handle regular nodes (exist in actual data structure)
            const pc = data.productionCapabilities.find(pc => pc.id === productionCapabilityId);
            if (!pc) return {...prev};
            
            const isDefault = pc.defaultNode?.id === handlingTier;
            if (isDefault && !isPSSD) {
                // Can't delete the default node on non-SSD sites
                return prev;
            }

            const node = isDefault ? pc.defaultNode : pc.additionalNodes.find(n => n.id === handlingTier);
            if (!node) return prev;
            
            // Add a delete change for existing nodes
            return {
                ...prev,
                [productionCapabilityId]: {
                    ...pcChanges,
                    [handlingTier]: {
                        productionCapability: pc.name,
                        handlingTier: node.handlingTier,
                        lowThreshold: node.lowThreshold ?? 0,
                        highThreshold: node.highThreshold ?? 100,
                        action: 'Delete',
                        isDefault
                    }
                }
            };
        });
    }, [data, isPSSD]);

    // Handle save action
    const handleSave = useCallback(async () => {
        // Check for invalid rows
        if (invalidRows.size > 0) {
            return;
        }
        
        // Flatten the pending changes into a single array
        const changes: PendingThresholdChange[] = [];
        
        Object.values(pendingChanges).forEach(pcChanges => {
            Object.values(pcChanges).forEach(change => {
                changes.push(change);
            });
        });
        
        // Call the onSave prop with the flattened changes
        // Only clear pending changes if save succeeds (doesn't throw)
        try {
            await onSave(changes);
            // Clear the pending changes only on success
            setPendingChanges({});
        }
        catch {
            // Validation or save failed — preserve pending rows so user isn't stuck
        }
    }, [pendingChanges, onSave, invalidRows]);

    // Count the number of pending changes
    const pendingChangeCount = useMemo(() => {
        return Object.values(pendingChanges)
            .reduce((count, pcChanges) => count + Object.keys(pcChanges).length, 0);
    }, [pendingChanges]);

    // Check if save button should be disabled
    const isSaveDisabled = invalidRows.size > 0 || pendingChangeCount === 0;

    // Extract all production capability names and node IDs for filters
    const availableProductionCapabilities = useMemo(() => {
        return Array.from(new Set(data.productionCapabilities.map(pc => pc.name)));
    }, [data]);
    
    const availableHandlingTiers = useMemo(() => {
        const handlingTiers = new Set<string>(['DEFAULT']);
        
        data.productionCapabilities.forEach(pc => {
            pc.additionalNodes.forEach(node => {
                handlingTiers.add(node.handlingTier);
            });
        });
        
        return Array.from(handlingTiers);
    }, [data]);

    // Apply filters and sorting to the data
    const filteredAndSortedPCs = useMemo(() => {
        let filtered = [...data.productionCapabilities];
        
        // Apply filters
        if (filters.productionCapability) {
            filtered = filtered.filter(pc => 
                pc.name.toLowerCase().includes(filters.productionCapability.toLowerCase())
            );
        }
        
        if (filters.handlingTier) {
            filtered = filtered.filter(pc => {
                // Check default node if it exists
                if (pc.defaultNode && pc.defaultNode.handlingTier === filters.handlingTier) {
                    return true;
                }
                
                return pc.additionalNodes.some(node =>
                    node.handlingTier === filters.handlingTier
                );
            });
        }
        
        if (filters.status) {
            filtered = filtered.filter(pc => {
                const soChanges = pendingChanges[pc.id];
                
                if (filters.status === 'unchanged' && !soChanges) {
                    return true;
                }
                
                if (filters.status === 'modified' && soChanges) {
                    return true;
                }
                
                return false;
            });
        }
        
        // First ensure network default PCs are at the top, then apply sorting
        return filtered.sort((a, b) => {
            // Always put network default PC at the top regardless of sort direction
            if (a.isNetworkDefault && !b.isNetworkDefault) return -1;
            if (!a.isNetworkDefault && b.isNetworkDefault) return 1;
            
            // If both are network default or both are not, continue with normal sorting
            let valA: any;
            let valB: any;
            
            switch (sorting.field) {
                case 'productionCapability':
                    valA = a.name;
                    valB = b.name;
                    break;
                case 'handlingTier':
                    // Sort by default handling tier if present
                    valA = a.defaultNode?.handlingTier || 'DEFAULT';
                    valB = b.defaultNode?.handlingTier || 'DEFAULT';
                    break;
                case 'lowThreshold':
                    valA = a.defaultNode?.lowThreshold ?? 0;
                    valB = b.defaultNode?.lowThreshold ?? 0;
                    break;
                case 'highThreshold':
                    valA = a.defaultNode?.highThreshold ?? 100;
                    valB = b.defaultNode?.highThreshold ?? 100;
                    break;
                case 'lastUpdated':
                    valA = a.defaultNode?.publishDateEpochMillis || 0;
                    valB = b.defaultNode?.publishDateEpochMillis || 0;
                    break;
                default:
                    valA = a.name;
                    valB = b.name;
            }
            
            // Handle string comparison
            if (typeof valA === 'string' && typeof valB === 'string') {
                return sorting.direction === 'asc' 
                    ? valA.localeCompare(valB) 
                    : valB.localeCompare(valA);
            }
            
            // Handle number comparison
            return sorting.direction === 'asc' 
                ? valA - valB 
                : valB - valA;
        });
    }, [data, filters, sorting, pendingChanges]);

    // Handle filter changes
    const handleFilterChange = useCallback((newFilters: FilterOptions) => {
        setFilters(newFilters);
    }, []);
    
    // Handle sort changes
    const handleSortChange = useCallback((newSorting: SortOption) => {
        setSorting(newSorting);
    }, []);

    return {
        // State
        expandedProductionCapabilities,
        pendingChanges,
        invalidRows,
        filters,
        sorting,
        pendingChangeCount,
        isSaveDisabled,
        availableProductionCapabilities,
        availableHandlingTiers,
        filteredAndSortedPCs,
        
        // Handlers
        handleToggleExpand,
        handleValueChange,
        handleAddNode,
        handleDeleteNode,
        handleSave,
        handleFilterChange,
        handleSortChange,
    };
};
